import { Routes, Route } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { Box, CircularProgress } from "@mui/material";
import { PrivateRoute } from "./PrivateRoute";
import PremiumGuard from "./PremiumGuard";
import DefaultLayout from "../components/Layout/DefaultLayout";
import CMSLayout from "../components/Layout/CMSLayout";
import HomeRedirect from "@/components/HomeRedirect";
import { ROUTES } from "../constants";

const PageLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
      width: "100%",
      height: "100%",
    }}
  >
    <CircularProgress sx={{ color: "#0D9488" }} size={36} thickness={4} />
  </Box>
);

const withLazy = <T extends React.ComponentType<any>>(importFn: () => Promise<{ default: T }>) => {
  const LazyComp = lazy(importFn);
  return (props: any) => (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyComp {...props} />
    </Suspense>
  );
};

// Lazy-loaded page components for route-level code-splitting with scoped Suspense
const ChallengePage = withLazy(() => import("@/pages/ChallengePage"));
const ProfilePage = withLazy(() => import("@/pages/ProfilePage"));
const AccountInfoPage = withLazy(() => import("@/pages/AccountInfoPage"));
const UsersPage = withLazy(() => import("@/pages/CMS/UsersPage"));
const TopicQuestionsPage = withLazy(() => import("@/pages/CMS/TopicsQuestionsPage"));
const Login = withLazy(() => import("@/pages/LoginPage"));
const NotFoundPage = withLazy(() => import("@/pages/NotFoundPage"));
const CategoryAspectPage = withLazy(() => import("@/pages/CMS/CategoryAspectPage"));
const QuestionIdeaPage = withLazy(() => import("@/pages/CMS/QuestionIdeaPage"));
const IdeaPage = withLazy(() => import("@/pages/LearnPage/components/IdeaPage"));
const ChallengeCMSPage = withLazy(() => import("@/pages/CMS/ChallengeCMSPage"));
const AnswerPage = withLazy(() => import("@/pages/LearnPage/components/AnswerPage"));
const WordbankPage = withLazy(() => import("@/pages/WordbankPage"));
const VocabularyPage = withLazy(() => import("@/pages/VocabularyPage"));
const WordbookPage = withLazy(() => import("@/pages/WordbookPage"));
const AllAspectPage = withLazy(() => import("@/pages/CMS/AllAspectPage"));
const PersonalizedCMSPage = withLazy(() => import("@/pages/CMS/PersonalizedCMSPage"));
const AICallStatisCMSPage = withLazy(() => import("@/pages/CMS/AICallStatisCMSPage"));
const GrammarCMSPage = withLazy(() => import("@/pages/CMS/GrammarCMSPage"));
const LearnPage = withLazy(() => import("@/pages/LearnPage"));
const QuestionPart2CMSPage = withLazy(() => import("@/pages/CMS/QuestionPart2CMSPage"));
const QuestionTypeCMSPage = withLazy(() => import("@/pages/CMS/QuestionTypeCMSPage"));
const QuestionTypePage = withLazy(() => import("@/pages/LearnPage/components/QuestionTypePage"));
const QuestionTypeListQuestionPage = withLazy(
  () => import("@/pages/LearnPage/components/QuestionTypeListQuestionPage")
);
const QuestionTypeByCatPage = withLazy(() => import("@/pages/LearnPage/components/QuestionTypeByCatPage"));
const GuidedQuestionPage = withLazy(() => import("@/pages/LearnPage/components/GuidedQuestionPage"));
const TopicQuestionTypePage = withLazy(() => import("@/pages/LearnPage/components/QuestionPart1&3Page"));
const Part2Page = withLazy(() => import("@/pages/LearnPage/components/Part2Page"));
const ClassPage = withLazy(() => import("@/pages/Teacher/ClassPage"));
const ClassDetailPage = withLazy(() => import("@/pages/Teacher/ClassDetailPage"));
const TeacherDashboardPage = withLazy(() => import("@/pages/Teacher/ClassOverviewPage"));
const MyClassPage = withLazy(() => import("@/pages/Student/MyClassPage"));
const LearnVocaCMSPage = withLazy(() => import("@/pages/CMS/LearnVocaCMSPage"));
const LearnVocaByQuestion = withLazy(() => import("@/pages/CMS/LearnVocaCMSPage/LearnVocaByQuestion"));
const WordbankCMSPage = withLazy(() => import("@/pages/CMS/WordbankCMSPage"));
const WordbookCMSPage = withLazy(() => import("@/pages/CMS/WordbankCMSPage/components/WordbookCMSPage"));
const QuestionWordbookCMSPage = withLazy(() => import("@/pages/CMS/QuestionWordbookCMSPage"));
const TeacherVocaAssignment = withLazy(() => import("@/pages/Teacher/TeacherVocaAssignment"));
const StudentVocaSubmisstion = withLazy(() => import("@/pages/Assignment/StudentVocaSubmisstion"));
const VocaSubmissionTracking = withLazy(() => import("@/pages/Teacher/TeacherVocaAssignment/VocaSubmissionTracking"));
const RegisterPage = withLazy(() => import("@/pages/RegisterPage"));
const GoogleAuthCallback = withLazy(() => import("@/pages/GoogleAuthCallback"));
const CoursePage = withLazy(() => import("@/pages/CoursePage"));
const CourseDetail = withLazy(() => import("@/pages/CoursePage/CourseDetail"));
const CourseCMSPage = withLazy(() => import("@/pages/CMS/CourseCMSPage"));
const CMSDetailCourse = withLazy(() => import("@/pages/CMS/CourseCMSPage/CMSDetailCourse"));
const GroupsPage = withLazy(() => import("@/pages/CMS/GroupsPage"));
const TopicOrQuestionType = withLazy(() => import("@/pages/LearnPage/components/TopicOrQuestionType"));
const PracticeSpeaking = withLazy(() => import("@/pages/LearnPage/components/PracticeSpeaking"));
const TimelineAnswerPage = withLazy(() => import("@/pages/LearnPage/components/TimelineAnswerPage"));
const TimelineLibraryPage = withLazy(() => import("@/pages/CMS/TimelineLibraryPage"));
const AITesterPage = withLazy(() => import("@/pages/CMS/AITesterPage"));
const DashboardPage = withLazy(() => import("@/pages/DashboardPage"));
const AdminDashboardPage = withLazy(() => import("@/pages/CMS/AdminDashboardPage"));
const PronunciationHistoryCMSPage = withLazy(() => import("@/pages/CMS/PronunciationHistoryCMSPage"));
const SpeechAceBillingCMSPage = withLazy(() => import("@/pages/CMS/SpeechAceBillingCMSPage"));
const AIBillingCMSPage = withLazy(() => import("@/pages/CMS/AIBillingCMSPage"));
const UserStatsPage = withLazy(() => import("@/pages/CMS/UserStatsPage"));
const UpgradePage = withLazy(() => import("@/pages/UpgradePage"));
const PaymentPage = withLazy(() => import("@/pages/PaymentPage"));
const SubscriptionPage = withLazy(() => import("@/pages/SubscriptionPage"));
const PackageCMSPage = withLazy(() => import("@/pages/CMS/PackageCMSPage"));
const TransactionCMSPage = withLazy(() => import("@/pages/CMS/TransactionCMSPage"));
const WebhookLogCMSPage = withLazy(() => import("@/pages/CMS/WebhookLogCMSPage"));
const EmailTemplateCMSPage = withLazy(() => import("@/pages/CMS/EmailTemplateCMSPage"));
const ForecastCMSPage = withLazy(() => import("@/pages/CMS/ForecastCMSPage"));
const AssessmentPage = withLazy(() => import("@/pages/AssessmentPage"));
const PronunciationAssessmentPage = withLazy(() => import("@/pages/PronunciationAssessmentPage"));
const QuestionAssessmentPage = withLazy(() => import("@/pages/QuestionAssessmentPage"));
const GrammarBookCMSPage = withLazy(() => import("@/pages/CMS/GrammarBookCMSPage"));
const GrammarLibraryPage = withLazy(() => import("@/pages/LearnPage/components/GrammarLibraryPage"));
const AspectStructureCMSPage = withLazy(() => import("@/pages/CMS/AspectStructureCMSPage"));
const CommonStructureCMSPage = withLazy(() => import("@/pages/CMS/CommonStructureCMSPage"));
const PromptManagementCMSPage = withLazy(() => import("@/pages/CMS/PromptManagementCMSPage"));
const UserReportsPage = withLazy(() => import("@/pages/CMS/UserReportsPage"));
const StudentAssignmentsPage = withLazy(() => import("@/pages/Student/AssignmentsPage"));
const StudentAssignmentDoPage = withLazy(() => import("@/pages/Student/AssignmentDoPage"));
const IeltsGeneratorDashboard = withLazy(() => import("@/pages/IeltsExamPage/components/IeltsGenerator"));
const IeltsSubmissionCMSPage = withLazy(() => import("@/pages/CMS/IeltsSubmissionCMSPage"));
const IeltsStudentDashboard = withLazy(() => import("@/pages/IeltsExamPage/components/IeltsStudentDashboard"));
const ReadingPracticeDashboard = withLazy(() => import("@/pages/IeltsExamPage/components/ReadingPracticeDashboard"));
const IeltsSimulator = withLazy(() => import("@/pages/IeltsExamPage/components/IeltsSimulator"));
const IeltsReview = withLazy(() => import("@/pages/IeltsExamPage/components/IeltsReview"));
const IeltsWritingTest = withLazy(() => import("@/pages/IeltsExamPage/components/IeltsWritingTest"));
const WritingQuestionsCMSPage = withLazy(() => import("@/pages/CMS/WritingQuestionsCMSPage"));
const SupplementaryExerciseCMS = withLazy(() => import("@/pages/SupplementaryExerciseCMS"));
const SupplementaryPracticePage = withLazy(() =>
  import("@/pages/SupplementaryPracticePage").then((module) => ({
    default: module.SupplementaryPracticePage,
  }))
);
const CambridgeFlashcardCMS = withLazy(() => import("@/pages/CambridgeFlashcardCMS"));
const SpeakingReactionTestPage = withLazy(() => import("@/pages/SpeakingReactionTestPage"));

const LandingRedirect = () => {
  useEffect(() => {
    window.location.replace("https://www.fongfluency.com");
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
};

const routerConfig = [
  {
    path: ROUTES.LANDING,
    element: <LandingRedirect />,
  },
  {
    path: "/",
    element: <HomeRedirect />,
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <DashboardPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LOGIN,
    element: <Login />,
  },
  {
    path: ROUTES.REGISTER,
    element: <RegisterPage />,
  },
  {
    path: ROUTES.GOOGLE_AUTH_CALLBACK,
    element: <GoogleAuthCallback />,
  },
  {
    path: ROUTES.USERS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <UsersPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.GROUPS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <GroupsPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TOPIC_QUESTION,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <TopicQuestionsPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.QUESTION_IDEA,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <QuestionIdeaPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CATEGORY_ASPECT,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <CategoryAspectPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CHALLENGE_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <ChallengeCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.PERSONALIZED_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <PersonalizedCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.GRAMMAR_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <GrammarCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.AI_CALL_STATIS_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AICallStatisCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_TIMELINE_LIBRARY,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <TimelineLibraryPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_AI_TESTER,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AITesterPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.QUESTION_TYPE_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <QuestionTypeCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.USER_STATS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <UserStatsPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  //   {
  //     path: ROUTES.LEARN,
  //     element: (
  //       <PrivateRoute>
  //         <DefaultLayout>
  //           <LearnPage />
  //         </DefaultLayout>
  //       </PrivateRoute>
  //     ),
  //   },
  {
    path: ROUTES.CHALLENGE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <ChallengePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.PROFILE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <ProfilePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ACCOUNT,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <AccountInfoPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  // PATH LEARN PAGE
  {
    path: ROUTES.LEARN,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <LearnPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_IDEA,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <IdeaPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_GUIDED_QUESTION_PART2,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <GuidedQuestionPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_QUESTION_TYPE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <QuestionTypePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_QUESTION_TYPE_BY_CAT,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <QuestionTypeByCatPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_QUESTION_TYPE_LIST_QUESTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <QuestionTypeListQuestionPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_PRACTICE_SPEAKING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PracticeSpeaking />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ANSWER,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <AnswerPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.WORDBANK,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <VocabularyPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ASSESSMENT,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <AssessmentPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ASSESSMENT_PRONUNCIATION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PronunciationAssessmentPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ASSESSMENT_QUESTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <QuestionAssessmentPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.SPEAKING_REACTION_TEST,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <SpeakingReactionTestPage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_WORDBANK,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <WordbankPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.WORDBOOK,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <WordbookPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_TOPIC_QUESTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <TopicQuestionTypePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ASPECT_ALL_CMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AllAspectPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.QUESTION_PART2,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <QuestionPart2CMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_LEARN_VOCA,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <LearnVocaCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_LEARN_VOCA_QUESTION,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <LearnVocaByQuestion />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_QUESTION_PAGE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <TimelineAnswerPage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_TOPIC_QUESTION_TYPE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <TopicOrQuestionType />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.ANSWER_QUESTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <AnswerPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_PART_2,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <Part2Page />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_DASHBOARD,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <TeacherDashboardPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_CLASS,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <ClassPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_CLASS_DETAIL,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <ClassDetailPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.STUDENT_MY_CLASS,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <MyClassPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.STUDENT_ASSIGNMENTS,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <StudentAssignmentsPage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.STUDENT_ASSIGNMENT_DETAIL,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <StudentAssignmentDoPage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_WORDBANK,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <WordbankCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_WORKBOOK,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <WordbookCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_QUESTION_WORDBOOK,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <QuestionWordbookCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_QUESTION_WORDBOOK,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <QuestionWordbookCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_VOCA_ASSIGNMENT,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <TeacherVocaAssignment />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.TEACHER_VOCA_SUBMISSION_TRACKING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <VocaSubmissionTracking />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.STUDENT_VOCA_SUBMISSTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <StudentVocaSubmisstion />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.COURSE_PAGE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <CoursePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.COURSE_DETAIL,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <CourseDetail />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_COURSE,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <CourseCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_DETAIL_COURSE,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <CMSDetailCourse />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_ADMIN_DASHBOARD,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AdminDashboardPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_PRONUNCIATION_HISTORY,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <PronunciationHistoryCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_SPEECHACE_BILLING,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <SpeechAceBillingCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_AI_BILLING,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AIBillingCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.UPGRADE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <UpgradePage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.PAYMENT,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PaymentPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.SUBSCRIPTION,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <SubscriptionPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_PACKAGE,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <PackageCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_TRANSACTION,
    name: "Transaction History",
    element: (
      <PrivateRoute>
        <CMSLayout>
          <TransactionCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_WEBHOOK_LOG,
    name: "Webhook Log",
    element: (
      <PrivateRoute>
        <CMSLayout>
          <WebhookLogCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_EMAIL_TEMPLATE,
    name: "Cấu hình Email",
    element: (
      <PrivateRoute>
        <CMSLayout>
          <EmailTemplateCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },

  {
    path: ROUTES.CMS_FORECAST,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <ForecastCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_GRAMMAR_BOOKS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <GrammarBookCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_IELTS_EXAMS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <IeltsGeneratorDashboard />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_IELTS_SUBMISSIONS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <IeltsSubmissionCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_WRITING_QUESTIONS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <WritingQuestionsCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_ASPECT_STRUCTURES,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <AspectStructureCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_COMMON_STRUCTURES,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <CommonStructureCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_PROMPT_MANAGEMENT,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <PromptManagementCMSPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_USER_REPORTS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <UserReportsPage />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_GRAMMAR,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <GrammarLibraryPage />
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LEARN_GRAMMAR_DETAIL,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <GrammarLibraryPage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsStudentDashboard />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_READING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsStudentDashboard module="reading" />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.READING_PRACTICE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <ReadingPracticeDashboard />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_LISTENING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsStudentDashboard module="listening" category="cambridge" />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_BASIC_LISTENING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsStudentDashboard module="listening" category="basic_listening" />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.LISTENING_PRACTICE,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <SupplementaryPracticePage />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_WRITING,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsWritingTest />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_TAKE,
    element: (
      <PrivateRoute>
        <PremiumGuard>
          <IeltsSimulator />
        </PremiumGuard>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.IELTS_EXAMS_REVIEW,
    element: (
      <PrivateRoute>
        <DefaultLayout>
          <PremiumGuard>
            <IeltsReview />
          </PremiumGuard>
        </DefaultLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_SUPPLEMENTARY_EXERCISES,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <SupplementaryExerciseCMS />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
  {
    path: ROUTES.CMS_CAMBRIDGE_FLASHCARDS,
    element: (
      <PrivateRoute>
        <CMSLayout>
          <CambridgeFlashcardCMS />
        </CMSLayout>
      </PrivateRoute>
    ),
  },
];
// CMS_DETAIL_COURSE
export function AllPages() {
  return (
    <Routes>
      {routerConfig.map((route, index) => {
        return <Route key={index} path={route.path} element={route.element} />;
      })}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
